import React, { useState } from 'react';
import Breadcrumbs from '../../components/BreadCrumbs';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import Table from '../../components/tables/Table';
import Button from '../../components/buttons/Button';
import { CustomCard } from '../../components/cards/CustomCard';
import SearchBar from '../../components/searchBar/SearchBar';
import DropDown from '../../components/inputFields/DropDown';
import { Link } from 'react-router-dom';
import { Edit3Icon, Package, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';

const ProductInventory = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');

    const [products, setProducts] = useState([
        {
            id: 1,
            name: 'Solar Home Kit',
            sku: 'SHK-001',
            category: 'Solar Systems',
            currentStock: 45,
            threshold: 25,
            unit: 'kits',
            supplier: 'Internal Assembly',
            costPerUnit: '₹5,500.00',
            sellingPrice: '₹7,500.00',
            totalValue: '₹3,37,500.00',
            lastRestocked: '2024-01-15',
            status: 'good',
            location: 'Warehouse A-01',
            profitMargin: '36.4%'
        },
        {
            id: 2,
            name: 'Irrigation Controller',
            sku: 'IRC-001',
            category: 'Irrigation',
            currentStock: 32,
            threshold: 20,
            unit: 'units',
            supplier: 'Internal Assembly',
            costPerUnit: '₹3,200.00',
            sellingPrice: '₹4,500.00',
            totalValue: '₹1,44,000.00',
            lastRestocked: '2024-01-18',
            status: 'good',
            location: 'Warehouse B-03',
            profitMargin: '40.6%'
        },
        {
            id: 3,
            name: 'Weather Station',
            sku: 'WST-001',
            category: 'Monitoring',
            currentStock: 18,
            threshold: 15,
            unit: 'units',
            supplier: 'Internal Assembly',
            costPerUnit: '₹2,850.00',
            sellingPrice: '₹4,200.00',
            totalValue: '₹75,600.00',
            lastRestocked: '2024-01-20',
            status: 'warning',
            location: 'Warehouse A-05',
            profitMargin: '47.4%'
        },
        {
            id: 4,
            name: 'Smart Pump Controller',
            sku: 'SPC-001',
            category: 'Pump Systems',
            currentStock: 12,
            threshold: 20,
            unit: 'units',
            supplier: 'Internal Assembly',
            costPerUnit: '₹1,950.00',
            sellingPrice: '₹2,800.00',
            totalValue: '₹33,600.00',
            lastRestocked: '2024-01-22',
            status: 'critical',
            location: 'Warehouse C-02',
            profitMargin: '43.6%'
        },
        {
            id: 5,
            name: 'Soil Moisture Sensor',
            sku: 'SMS-001',
            category: 'Sensors',
            currentStock: 28,
            threshold: 25,
            unit: 'units',
            supplier: 'Internal Assembly',
            costPerUnit: '₹1,200.00',
            sellingPrice: '₹1,800.00',
            totalValue: '₹50,400.00',
            lastRestocked: '2024-01-19',
            status: 'good',
            location: 'Warehouse B-01',
            profitMargin: '50.0%'
        },
        {
            id: 6,
            name: 'Automated Fertilizer Dispenser',
            sku: 'AFD-001',
            category: 'Fertilization',
            currentStock: 8,
            threshold: 15,
            unit: 'units',
            supplier: 'Internal Assembly',
            costPerUnit: '₹4,500.00',
            sellingPrice: '₹6,500.00',
            totalValue: '₹52,000.00',
            lastRestocked: '2024-01-16',
            status: 'critical',
            location: 'Warehouse A-08',
            profitMargin: '44.4%'
        }
    ]);

    const categories = [
        { value: 'all', label: 'All Categories' },
        { value: 'Solar Systems', label: 'Solar Systems' },
        { value: 'Irrigation', label: 'Irrigation' },
        { value: 'Monitoring', label: 'Monitoring' },
        { value: 'Pump Systems', label: 'Pump Systems' },
        { value: 'Sensors', label: 'Sensors' },
        { value: 'Fertilization', label: 'Fertilization' }
    ];

    const statusOptions = [
        { value: 'all', label: 'All Status' },
        { value: 'critical', label: 'Critical' },
        { value: 'warning', label: 'Warning' },
        { value: 'good', label: 'Good Stock' }
    ];

    const getStatusBadge = (status) => {
        const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
        switch (status) {
            case 'critical':
                return `${baseClasses} bg-red-100 text-red-800`;
            case 'warning':
                return `${baseClasses} bg-yellow-100 text-yellow-800`;
            case 'good':
                return `${baseClasses} bg-green-100 text-green-800`;
            default:
                return `${baseClasses} bg-gray-100 text-gray-800`;
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'critical':
                return <AlertTriangle className="w-4 h-4 text-red-600" />;
            case 'warning':
                return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
            case 'good':
                return <CheckCircle className="w-4 h-4 text-green-600" />;
            default:
                return <Package className="w-4 h-4 text-gray-600" />;
        }
    };

    const filteredProducts = products.filter(product => {
        const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.sku.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = filterCategory === 'all' || product.category === filterCategory;
        const matchesStatus = filterStatus === 'all' || product.status === filterStatus;

        return matchesSearch && matchesCategory && matchesStatus;
    });

    return (
        <>
            <Breadcrumbs items={[
                { name: 'Home', link: '/dashboard' },
                { name: 'Inventory' },
                { name: 'Products' },
            ]} />

            <div className="flex justify-between items-center mb-6">
                <StyledHeadingText text={"Product Inventory"} size={"text-3xl"} />
                <Link
                    className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg flex items-center gap-2"
                    to="/inventory/products/add"
                >
                    <Package className="w-4 h-4" />
                    Add New Product
                </Link>
            </div>

            {/* Filters Section */}
            <CustomCard title="Filters & Search">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="md:col-span-2">
                        <SearchBar
                            value={searchTerm}
                            onSearch={setSearchTerm}
                            placeholder="Search by product name or SKU..."
                        />
                    </div>
                    <DropDown
                        options={categories}
                        value={filterCategory}
                        onChange={setFilterCategory}
                        placeholder="Filter by Category"
                    />
                    <DropDown
                        options={statusOptions}
                        value={filterStatus}
                        onChange={setFilterStatus}
                        placeholder="Filter by Status"
                    />
                </div>
            </CustomCard>

            {/* Products Table */}
            <CustomCard title={`Products (${filteredProducts.length})`}>
                <Table
                    headers={[
                        { label: "Product Details", key: "product" },
                        { label: "Stock Status", key: "stock" },
                        { label: "Location", key: "location" },
                        { label: "Last Updated", key: "updated" },
                        { label: "Actions", key: "actions" }
                    ]}
                    rows={filteredProducts.map(product => ({
                        product: (
                            <div className="flex flex-col">
                                <div className="font-semibold text-gray-900">{product.name}</div>
                                <div className="text-sm text-gray-500">SKU: {product.sku}</div>
                            </div>
                        ),
                        stock: (
                            <div className="flex items-center gap-2">
                                {getStatusIcon(product.status)}
                                <div className="flex flex-col">
                                    <span className="font-medium">{product.currentStock} {product.unit}</span>
                                    <span className={`text-xs ${product.status === 'critical' ? 'text-red-600' :
                                        product.status === 'warning' ? 'text-yellow-600' : 'text-green-600'}`}>
                                        Threshold: {product.threshold}
                                    </span>
                                </div>
                            </div>
                        ),
                        location: (
                            <div className="text-sm text-gray-600">{product.location}</div>
                        ),
                        updated: (
                            <div className="text-sm text-gray-500">{product.lastRestocked}</div>
                        ),
                        actions: (
                            <div className="flex gap-2">
                                <Link
                                    to={`/inventory/products/${product.id}`}
                                    className="p-1 text-blue-600 hover:text-blue-800"
                                >
                                    <Edit3Icon className="w-4 h-4" />
                                </Link>
                            </div>
                        )
                    }))}
                />
            </CustomCard>
        </>
    );
};

export default ProductInventory; 